import React, { useMemo } from 'react';
import PhoneInputBase, { getCountryCallingCode } from 'react-phone-number-input';
import es from 'react-phone-number-input/locale/es.json';
import flags from 'react-phone-number-input/flags';
import Select from 'react-select';
import { selectStyles } from '../../utils/adminShared';
import 'react-phone-number-input/style.css';

// Normaliza un string para busqueda tolerante: minusculas + sin diacriticos.
// Asi "mexico" encuentra "México", "peru" encuentra "Perú", etc.
const normalizarBusqueda = (s) => String(s || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '');

// Renderiza la bandera SVG del pais (usa los SVG oficiales de la lib —
// funcionan en Windows/Linux/Mac sin depender de la fuente del sistema).
// Contenedor con proporcion 3:2 fija para no deformar y quedar centrada.
const CountryFlag = ({ code, width = 22 }) => {
    const Flag = code && flags[code];
    const height = Math.round((width / 3) * 2);
    const style = {
        display: 'inline-block',
        width,
        height,
        flexShrink: 0,
        borderRadius: 2,
        overflow: 'hidden',
        lineHeight: 0,
        verticalAlign: 'middle'
    };
    if (!Flag) return <span style={style}>🌐</span>;
    return (
        <span style={style} aria-hidden="true">
            <Flag title={code} />
        </span>
    );
};

/**
 * CountrySelect customizado: reemplaza al <select> nativo por un react-select
 * con buscador, banderas y nombres en español. Se pasa como `countrySelectComponent`
 * a PhoneInputBase. La firma la impone react-phone-number-input.
 */
const CountrySelect = ({ value, onChange, options, disabled, iconComponent, ...rest }) => {
    const items = useMemo(() => {
        return (options || [])
            .filter(o => o && o.value)
            .map(o => ({
                value: o.value,
                label: o.label,
                callingCode: (() => {
                    try { return getCountryCallingCode(o.value); }
                    catch { return null; }
                })()
            }));
    }, [options]);

    const selected = value ? items.find(i => i.value === value) : null;

    return (
        <div className="don-phone-country-wrap">
        <Select
            className="don-phone-country-select"
            classNamePrefix="don-phone-country"
            styles={selectStyles}
            options={items}
            value={selected}
            onChange={(opt) => onChange(opt?.value || undefined)}
            isDisabled={disabled}
            isSearchable
            menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
            menuPosition="fixed"
            placeholder="País…"
            noOptionsMessage={() => 'No se encontró ningún país'}
            // Buscador: filtra por nombre del pais y tambien por el codigo
            // de marcado si el usuario tipea numeros (ej. "51" -> Peru).
            filterOption={(candidate, input) => {
                if (!input) return true;
                const q = normalizarBusqueda(input);
                return (
                    normalizarBusqueda(candidate.label).includes(q)
                    || String(candidate.data.callingCode || '').includes(q.replace('+', ''))
                    || String(candidate.data.value || '').toLowerCase().includes(q)
                );
            }}
            formatOptionLabel={(opt, meta) => (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                    <CountryFlag code={opt.value} />
                    {/* Menu: bandera + nombre + (+XX). Value cerrado: bandera + (+XX). */}
                    {meta.context === 'menu' && (
                        <span style={{ flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {opt.label}
                        </span>
                    )}
                    {opt.callingCode && (
                        <span style={{
                            color: 'rgb(var(--text-muted))',
                            fontSize: 12,
                            fontWeight: 500,
                            whiteSpace: 'nowrap'
                        }}>
                            (+{opt.callingCode})
                        </span>
                    )}
                </span>
            )}
            {...rest}
        />
        </div>
    );
};

/**
 * Wrapper del componente `react-phone-number-input` alineado al design system
 * del proyecto (tokens light/dark, focus con `--accent-cyan` como los demas
 * inputs, banderas emoji nativas del sistema).
 *
 * Uso:
 *   <PhoneInput value={form.telefono} onChange={v => setForm({...form, telefono: v || ''})} />
 *
 * `value` y el evento `onChange` trabajan siempre en formato E.164:
 *   +51987654321   (no lleva espacios ni parentesis)
 *
 * El input muestra el numero formateado internamente pero al guardar solo
 * viaja el E.164 — asi busquedas y reportes se hacen sobre un formato unico.
 */
const PhoneInput = React.forwardRef(({
    value,
    onChange,
    placeholder = 'Ej. 987 654 321',
    defaultCountry = 'PE',
    disabled = false,
    className = '',
    ...rest
}, ref) => (
    <PhoneInputBase
        ref={ref}
        // `international={false}` -> el input SOLO muestra el numero nacional
        // formateado (ej. "987 654 321"), sin el "+51" al principio. El codigo
        // vive en el selector de pais (bandera + (+XX)) y aparece en el value
        // final que emite `onChange` en formato E.164 completo.
        international={false}
        countryCallingCodeEditable={false}
        defaultCountry={defaultCountry}
        placeholder={placeholder}
        value={value || undefined}
        onChange={(v) => onChange?.(v || '')}
        disabled={disabled}
        className={`don-phone-input ${className}`}
        // Limita al maximo de digitos por pais (PE 9, AR 10, US 10, etc).
        limitMaxLength
        // Nombres de paises en español + select custom con buscador y banderas.
        labels={es}
        countrySelectComponent={CountrySelect}
        flags={undefined}
        {...rest}
    />
));

PhoneInput.displayName = 'PhoneInput';
export default PhoneInput;
